import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'

import { ApiError, MessageGroup, PaginatedApiResult } from '@mangovoice/types'

/**
 * Fetches all messaging groups for a PBX that can be used in switchboard call flow nodes.
 * Messaging groups are used in SMS nodes to send text messages to groups of recipients.
 *
 * Uses /app/v1/message-groups/ to match admin3 exactly.
 */
export const useGetMessagingGroups = (pbxId: number | null) => {
  const api = useMangoApiClient()

  const {
    data: messagingGroupsResponse,
    isLoading,
    isSuccess,
  } = useQuery<PaginatedApiResult<MessageGroup> | null, ApiError>({
    queryKey: ['switchboard', 'messageGroups', pbxId],
    queryFn: async () => {
      if (!pbxId) return null

      const response = await api.switchboard.listMessageGroups({
        pbx_id: pbxId,
        limit: 200,
        offset: 0,
      })

      if (response.ok && response.data) {
        return response.data
      }

      return null
    },
    enabled: !!pbxId,
    retry: false,
  })

  const messagingGroups = useMemo(() => {
    return messagingGroupsResponse?.results || []
  }, [messagingGroupsResponse])

  return {
    messagingGroups,
    isLoading,
    isLoaded: isSuccess && messagingGroupsResponse !== null,
  }
}
